import styled, { useTheme } from 'styled-components';
import { useDispatch, useSelector } from 'react-redux';
import { ParagraphLargeText, ParagraphSmallText } from '../Texts';
import { fromTokenAmount } from '../../hooks/useSolana';
import { selectTokenByNetworkAndAddress } from '../../redux/slices/tokenSlice';
import StatusBadge from '../StatusBadge';
import { openModal } from '../../redux/slices/modals';
import IconButton from '../IconButton';
import { formatTokenPrice } from '../../common/currency';
import { selectThemeMode } from '../../redux/slices/themeSlice';
import { Capacitor } from '@capacitor/core';
import { Share } from '@capacitor/share';

const CardContainer = styled.div`
  position: relative;
  width: 100%;
  padding-top: calc(100% * 226 / 407);
  background-color: ${({ theme }) => theme.background.primary};
  border-radius: 16px;
  background-image: ${({ $background }) =>
    $background ? `url(${$background})` : 'none'};
  background-size: cover;
  background-position: center;
`;

const TokenDetailsContainer = styled.div`
  border-radius: 16px;
  left: 15px;
  right: 15px;
  display: flex;
  flex-direction: column;
  padding: 12px;
  gap: 2px;
`;

const TokenBalanceContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 5px;
  flex: 1;
`;

const Container = styled.div`
  border: 1px solid ${({ theme }) => theme.border.primary};
  background-color: ${({ theme }) => theme.background.primary};
  border-radius: 16px;
`;

const GiftedCard = ({ card, received }) => {
  const tokenDetails = useSelector((state) =>
    selectTokenByNetworkAndAddress(state, card?.network, card?.token),
  );

  const formattedAmount = formatTokenPrice(
    fromTokenAmount(card.tokenAmount, tokenDetails?.decimals),
    card.purchaseUsdValue,
  );

  const dispatch = useDispatch();

  const theme = useTheme();

  const selectedTheme = useSelector(selectThemeMode);

  return (
    <Container>
      <CardContainer $background={card?.imageUrl} />
      <TokenDetailsContainer>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
          }}
        >
          <TokenBalanceContainer>
            <div style={{ display: 'flex' }}>
              <ParagraphLargeText>
                {formattedAmount} {card.symbol}
                {tokenDetails?.name}
              </ParagraphLargeText>
              <ParagraphLargeText $color={theme.text.secondary}>
                &nbsp;{tokenDetails?.symbol}
              </ParagraphLargeText>
            </div>
          </TokenBalanceContainer>
          <img src={tokenDetails?.logoURI} style={{ width: 40, height: 40 }} />
        </div>
        <TokenBalanceContainer>
          {card?.type === 'PHYSICAL' ? (
            <>Physical Gift</>
          ) : (
            <>
              <ParagraphSmallText $color={theme.text.secondary}>
                {card?.htlcExpiry ? 'Link Gift' : 'Sent To'}
              </ParagraphSmallText>
              {!card?.htlcExpiry && (
                <div style={{ display: 'flex' }}>
                  <ParagraphSmallText>
                    {card?.receiver?.email || 'Pending Claim'}
                  </ParagraphSmallText>
                </div>
              )}
            </>
          )}
        </TokenBalanceContainer>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          {!!card?.status && (
            <StatusBadge
              status={card.status}
              type={card?.type}
              deliveryStatus={card?.shippingDetails?.status}
            />
          )}
          {card?.type === 'PHYSICAL' ? (
            <></>
          ) : (
            <>
              {card.status === 'HTLC_CREATED' && card?.claimUrl && (
                <IconButton
                  iconUrl={'/share.svg'}
                  variant='square-secondary'
                  imageStyle={
                    selectedTheme === 'dark'
                      ? { filter: 'brightness(0) invert(1)' }
                      : {}
                  }
                  onClick={() => {
                    if (
                      Capacitor.isNativePlatform() &&
                      Capacitor.isPluginAvailable('Share')
                    ) {
                      Share.share({
                        url: card?.claimUrl,
                      });
                    } else {
                      dispatch(
                        openModal({
                          name: 'shareGift',
                          props: { claimUrl: card?.claimUrl },
                        }),
                      );
                    }
                  }}
                />
              )}
            </>
          )}
        </div>
      </TokenDetailsContainer>
    </Container>
  );
};

export default GiftedCard;
